import axios from 'axios';
import serviceUtil from './serviceUtil';

const BASE_URI = '/progress_reports';
const TIMEOUT = 20000;

/**
 * Ensure user has a full display name from first_name + last_name (avoids truncated API "name").
 */
const ensureFullUserName = (user) => {
  if (!user) return;
  const first = (user.first_name || '').trim();
  const last = (user.last_name || '').trim();
  if (first && last) {
    user.name = first + ' ' + last;
  }
};

/**
 * Transform JSON:API response to flat objects with joined user data
 */
const transformProgressReport = (item, usersMap = {}) => {
  const report = item.attributes ? { id: item.id, ...item.attributes } : item;
  if (report.user_id && usersMap[report.user_id]) {
    report.user = usersMap[report.user_id];
    ensureFullUserName(report.user);
  }
  return report;
};

const buildUsersMap = (included = []) => {
  const usersMap = {};
  included.forEach(item => {
    if (item.type === 'user' && item.attributes) {
      const attrs = { ...item.attributes };
      ensureFullUserName(attrs);
      usersMap[item.id] = attrs;
    }
  });
  return usersMap;
};

const getHeaders = () => ({
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  'Pragma': 'no-cache',
  'Expires': '0',
  'Content-Type': 'application/json',
  'Accept': '*/*',
  'Authorization': `Token token=${localStorage.token}`,
});

const buildUrl = (path = '', queryParams = {}) => {
  const params = new URLSearchParams({
    current_company: localStorage.current_company_id,
    ...queryParams,
  });
  return `${process.env.REACT_APP_API}${BASE_URI}${path}?${params}`;
};

const request = (method, path = '', { queryParams, data } = {}) => {
  return axios({
    method,
    url: buildUrl(path, queryParams),
    timeout: TIMEOUT,
    headers: getHeaders(),
    data,
  })
    .then(response => response.data)
    .catch(error => serviceUtil.resultForError(error));
};

/**
 * Create UTC offset string for timezone fallback (e.g., "+0500" or "-0700")
 */
const createOffsetTimezone = () => {
  let offset = new Date().getTimezoneOffset() / 60 * 100;
  let offsetString = offset < 0 ? '+' : '-';
  const absOffset = Math.abs(offset);
  offsetString += absOffset.toString().length > 3 ? absOffset : `0${absOffset}`;
  return offsetString;
};

/**
 * Create a progress report for a project within a date range
 */
const createProgressReport = ({ project_id, start_date, end_date }) => {
  const data = { project_id, start_date, end_date };
  
  // Include timezone info (same pattern as closeouts)
  let timezone = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    // ignore - will use offset fallback
  }
  
  if (timezone) {
    data.timezone = timezone;
  } else {
    data.offset = createOffsetTimezone();
  }
  
  return request('post', '', { data });
};

/**
 * Get a progress report by ID (returns transformed data)
 */
const getProgressReport = async (progressReportId) => {
  const response = await request('get', `/${progressReportId}`);
  if (response && !response.error) {
    const data = response.data || response;
    const included = response.included || [];
    const usersMap = buildUsersMap(included);
    return {
      ...response,
      progressReport: transformProgressReport(data, usersMap),
    };
  }
  return response;
};

/**
 * Get progress reports for a project (returns transformed data)
 * Handles: response.data (array), response.progress_reports, response.data.progress_reports
 */
const getProgressReportsByProject = async (projectId) => {
  const response = await request('get', '', { queryParams: { project_id: projectId } });
  if (response && !response.error) {
    let data = response.data;
    if (!Array.isArray(data)) {
      data = response.progress_reports ||
        (response.data && response.data.progress_reports) ||
        [];
    }
    const included = response.included || [];
    const usersMap = buildUsersMap(included);
    const progressReports = Array.isArray(data)
      ? data.map(item => transformProgressReport(item, usersMap))
      : [];
    return { ...response, progressReports };
  }
  return response;
};

/**
 * Delete a progress report
 */
const deleteProgressReport = (progressReportId) => {
  return request('delete', `/${progressReportId}`);
};

/**
 * Optimistically delete a progress report with rollback on error
 * @param {Object} options
 * @param {string} options.progressReportId - ID of the report to delete
 * @param {Array} options.progressReports - Current array of progress reports
 * @param {Function} options.setState - Component's setState function
 * @param {Object} options.history - React Router history object
 * @param {string} options.location_id - Location ID for navigation
 * @param {string} options.project_id - Project ID for navigation
 */
const deleteProgressReportOptimistic = ({ 
  progressReportId, 
  progressReports, 
  setState, 
  history, 
  location_id, 
  project_id, 
}) => {
  if (!progressReports) return;
  
  const reportIndex = progressReports.findIndex(pr => pr.id == progressReportId);
  if (reportIndex === -1) return;
  
  // Optimistic update
  const deletedReport = progressReports.splice(reportIndex, 1)[0];
  setState({ progressReports: [...progressReports], selectedAttachment: null });
  
  // Navigate
  history.push('/por/project/' + project_id + '/location/all?tab=progress_reports');
  
  // API call with rollback on error
  deleteProgressReport(progressReportId)
    .catch(error => {
      progressReports.splice(reportIndex, 0, deletedReport);
      setState({ progressReports: [...progressReports] });
      const dataModel = require('../models/DataModel').default;
      dataModel.displayHTTPError('Error deleting progress report', error, false);
    });
};

export default {
  createProgressReport,
  getProgressReport,
  getProgressReportsByProject,
  deleteProgressReport,
  deleteProgressReportOptimistic,
};



// WEBPACK FOOTER //
// ./src/services/progressReportsService.js